// root's chat (D-060): the conversation for the current store, kept for this browser session so it
// survives closing and reopening the panel (a "New chat" clears it). One reply per message from
// POST …/ai/chat; until AI is connected the server answers 503 activation_required and root says so
// in the chat instead of pretending.
import { t } from "@ady/i18n";
import { useQuery } from "@tanstack/react-query";
import { useSyncExternalStore } from "react";
import { ApiError, request } from "../api/client";
import { errorMessage } from "../api/errors";

export interface RootMessage {
  id: string;
  role: "user" | "root";
  text: string;
  /** root's reply is an error notice (not connected, failed), not an answer. */
  notice?: boolean;
}

interface ChatState {
  key: string;
  messages: RootMessage[];
  pending: boolean;
}

const EMPTY: ChatState = { key: "", messages: [], pending: false };
const listeners = new Set<() => void>();
let state: ChatState = EMPTY;

function set(next: ChatState) {
  state = next;
  for (const fn of listeners) fn();
}

const keyOf = (org: string, store: string) => `${org}/${store}`;

/** The chat for this store (another store's chat is never shown). */
export function useRootChat(org: string, store: string): ChatState {
  const snap = useSyncExternalStore(
    (fn) => {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
    () => state,
  );
  return snap.key === keyOf(org, store) ? snap : EMPTY;
}

/**
 * Is AI connected? From AI-05 usage (`availability`); "unknown" while loading or when this member
 * cannot read usage (billing.read). A not-connected reply in the chat also refreshes it.
 */
export function useRootAvailability(org: string): "available" | "activation_required" | "unknown" {
  const q = useQuery({
    queryKey: ["ai", "usage", org],
    queryFn: () =>
      request<{ availability: "available" | "activation_required" }>(
        "GET",
        `/orgs/${encodeURIComponent(org)}/ai/usage`,
      ),
    staleTime: 60_000,
    retry: false,
  });
  return q.data?.availability ?? "unknown";
}

export function clearRootChat() {
  if (!state.pending) set(EMPTY);
}

const id = () => crypto.randomUUID();

export async function sendToRoot(org: string, store: string, text: string): Promise<void> {
  const key = keyOf(org, store);
  const base = state.key === key ? state.messages : [];
  if (state.pending && state.key === key) return;
  const messages: RootMessage[] = [...base, { id: id(), role: "user", text }];
  set({ key, messages, pending: true });
  let reply: RootMessage;
  try {
    const res = await request<{ reply: string }>(
      "POST",
      `/orgs/${encodeURIComponent(org)}/stores/${encodeURIComponent(store)}/ai/chat`,
      {
        // Earlier notices are not part of the conversation the model sees.
        body: {
          messages: messages
            .filter((m) => !m.notice)
            .slice(-40)
            .map((m) => ({ role: m.role, text: m.text })),
        },
      },
    );
    reply = { id: id(), role: "root", text: res.reply };
  } catch (err) {
    const off = err instanceof ApiError && err.code === "dependency.activation_required";
    // D-064: out of plan credits and bought ones → root points to the Adyvance Wallet.
    const out = err instanceof ApiError && err.code === "entitlement.quota_exceeded";
    reply = {
      id: id(),
      role: "root",
      text: off
        ? t("aiMascot.chat.notConnected")
        : out
          ? t("aiMascot.chat.noCredits")
          : errorMessage(err),
      notice: true,
    };
  }
  // The store may have changed or the chat been cleared while waiting: keep the reply only here.
  if (state.key !== key) return;
  set({ key, messages: [...state.messages, reply], pending: false });
}
